import { z } from 'zod';
import { cursorQuerySchema, pageSchema, timestampSchema } from './admin.js';
import { colorSchema, isoDateSchema, personSchema } from './projects.js';

/** COL-05: in-app notifications (bell) and e-mail per user preference. */

export const notificationTypeSchema = z.enum([
  'assigned',
  'mentioned',
  'commented',
  'status_changed',
  'due_soon',
]);
export type NotificationType = z.infer<typeof notificationTypeSchema>;
export const notificationTypes = notificationTypeSchema.options;

/**
 * E-mail delivery per user: right away (checked every minute), as a digest every 30 minutes,
 * daily at 07:00 Europe/Berlin (E5) or not at all. In-app notifications are always created.
 */
export const emailModeSchema = z.enum(['immediate', 'digest_30m', 'daily', 'off']);
export type EmailMode = z.infer<typeof emailModeSchema>;

const typeSwitches = Object.fromEntries(
  notificationTypes.map((type) => [type, z.boolean().default(true)]),
) as Record<NotificationType, z.ZodDefault<z.ZodBoolean>>; // fromEntries over the full enum

/** Stored in `users.notification_prefs`; missing values fall back to the defaults. */
export const notificationPrefsSchema = z.object({
  email: emailModeSchema.default('immediate'),
  /** E-mail per notification type (the bell always shows everything). */
  types: z.object(typeSwitches).prefault({}),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

/** Preferences from the stored JSON; invalid or missing values become the defaults. */
export function parseNotificationPrefs(stored: unknown): NotificationPrefs {
  const parsed = notificationPrefsSchema.safeParse(stored ?? {});
  return parsed.success ? parsed.data : notificationPrefsSchema.parse({});
}

/** Whether a notification of this type goes out by e-mail in this delivery run. */
export function wantsEmail(prefs: NotificationPrefs, mode: EmailMode, type: NotificationType) {
  return prefs.email === mode && mode !== 'off' && prefs.types[type];
}

/** Length of comment excerpts in notifications and e-mails. */
export const NOTIFICATION_EXCERPT_LENGTH = 300;

/**
 * Snapshot written when the notification is created, prepared for its recipient (hidden people
 * per E-08 are already hidden). The task link still checks visibility when opened.
 */
export const notificationPayloadSchema = z.object({
  projectKey: z.string(),
  /** Missing in notifications created before the styled e-mails. */
  projectName: z.string().nullable().default(null),
  taskKey: z.string(),
  taskTitle: z.string(),
  actor: personSchema.nullable(),
  /** Comment events: the comment (for re-checking its visibility) and an excerpt. */
  commentId: z.uuid().nullable().default(null),
  comment: z.string().nullable().default(null),
  /** Status names for `status_changed`. */
  status: z
    .object({ from: z.string().nullable(), to: z.string().nullable() })
    .nullable()
    .default(null),
  /** Status colors for `status_changed` (e-mail only; missing in older notifications). */
  statusColors: z
    .object({ from: colorSchema.nullable(), to: colorSchema.nullable() })
    .nullable()
    .default(null),
  dueDate: isoDateSchema.nullable().default(null),
});
export type NotificationPayload = z.infer<typeof notificationPayloadSchema>;

export const notificationSchema = z.object({
  id: z.uuid(),
  type: notificationTypeSchema,
  taskId: z.uuid(),
  projectKey: z.string(),
  taskKey: z.string(),
  taskTitle: z.string(),
  actor: personSchema.nullable(),
  comment: z.string().nullable(),
  status: z.object({ from: z.string().nullable(), to: z.string().nullable() }).nullable(),
  dueDate: isoDateSchema.nullable(),
  readAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationListQuerySchema = cursorQuerySchema.extend({
  unread: z.stringbool().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

export const notificationListResponseSchema = pageSchema(notificationSchema);
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

export const unreadCountResponseSchema = z.object({ count: z.number().int() });
export type UnreadCountResponse = z.infer<typeof unreadCountResponseSchema>;

export const notificationIdParamsSchema = z.object({ notificationId: z.uuid() });

/** Retention (E8): read notifications 90 days, unread ones 180 days. */
export const NOTIFICATION_RETENTION_READ_DAYS = 90;
export const NOTIFICATION_RETENTION_UNREAD_DAYS = 180;
